import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { getSEOData } from '../utils/seoData';
import { CLINICAL_DISCLAIMER } from '../data/ewm';
import './Narrative.css';

function Partners() {
  return (
    <div className="np-page">
      <SEO {...getSEOData('partners')} />

      {/* Hero */}
      <section className="np-hero">
        <div className="container">
          <span className="np-eyebrow">Design Partner Program</span>
          <h1>Run the loop on your own model, in your own infrastructure</h1>
          <p className="np-hero-sub">
            The mechanism is measured. What it needs next is your data, your vocabulary and your
            decoder — because integration with a real host is the only test that counts.
          </p>
          <div className="np-cta-row np-cta-row--center">
            <Link to="/contact" className="np-btn np-btn--ghost">Start a conversation</Link>
          </div>
        </div>
      </section>

      {/* What a design partnership is */}
      <section className="np-section">
        <div className="container">
          <h2 className="np-h2">What a design partnership looks like</h2>
          <p className="np-lead">
            We are looking for a small number of partners with real documents and a real
            constraint — privacy, cost or trust — that makes today&rsquo;s model stack fail.
          </p>
          <div className="np-grid np-grid--2">
            <div className="np-card">
              <h3 className="np-h3">You bring</h3>
              <ul className="np-list">
                <li>A host model or pipeline you already run</li>
                <li>Real documents or streams, in your own environment</li>
                <li>A constraint: data residency, per-token cost, or auditability</li>
                <li>A technical contact who can work with us on the integration</li>
              </ul>
            </div>
            <div className="np-card">
              <h3 className="np-h3">We bring</h3>
              <ul className="np-list">
                <li>The side-car implementation and the adapter contract</li>
                <li>Engineering time to stand the loop up against your host</li>
                <li>Measured before/after results on recall, novelty and context cost</li>
                <li>Direct access to the people who built the algebra</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* What we measure */}
      <section className="np-section np-section--alt">
        <div className="container">
          <h2 className="np-h2">What we measure together</h2>
          <p className="np-lead">
            Each engagement exits on a number, not a milestone label.
          </p>
          <div className="np-grid np-grid--4">
            <div className="np-card">
              <h3 className="np-h3">Round-trip fidelity</h3>
              <p className="np-body">
                Hash-level and order-level accuracy of the side-car loop against your host&rsquo;s
                own encodings.
              </p>
            </div>
            <div className="np-card">
              <h3 className="np-h3">Recall &amp; novelty</h3>
              <p className="np-body">
                Structural recall and D/R/N novelty measured against context-window and
                retrieval-augmented baselines.
              </p>
            </div>
            <div className="np-card">
              <h3 className="np-h3">Context cost</h3>
              <p className="np-body">
                Before/after cost and latency on your long-horizon tasks, on the hardware you
                actually run.
              </p>
            </div>
            <div className="np-card">
              <h3 className="np-h3">Deployment fit</h3>
              <p className="np-body">
                Footprint, storage and recovery behaviour in your own infrastructure — including
                air-gapped environments.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Where we are */}
      <section className="np-section">
        <div className="container">
          <h2 className="np-h2">Where the program is today</h2>
          <div className="np-grid np-grid--3">
            <div className="np-card np-card--highlight">
              <span className="np-badge">Now</span>
              <h3 className="np-h3">Two design partners</h3>
              <p className="np-body">
                Running the loop on their own models, in their own infrastructure.
              </p>
            </div>
            <div className="np-card">
              <span className="np-badge np-badge--muted">Next</span>
              <h3 className="np-h3">Side-car SDK v1</h3>
              <p className="np-body">
                Across three host families — text, vision and OCR — with a stable adapter contract.
              </p>
            </div>
            <div className="np-card">
              <span className="np-badge np-badge--muted">Then</span>
              <h3 className="np-h3">Payable pilots</h3>
              <p className="np-body">
                Measured before/after context cost as the basis for a commercial engagement.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="np-section np-section--alt">
        <div className="container">
          <div className="np-disclaimer" style={{ marginBottom: '34px' }}>{CLINICAL_DISCLAIMER}</div>
          <h2 className="np-h2 np-h2--center">Tell us about your host model</h2>
          <p className="np-lead np-lead--center">
            If you have a model you cannot retrain, documents that cannot leave the building, and a
            cost curve you need to flatten — that is exactly the shape of the problem we solve.
          </p>
          <div className="np-cta-row np-cta-row--center">
            <Link to="/contact" className="np-btn np-btn--primary">Become a design partner</Link>
            <Link to="/evidence" className="np-btn np-btn--secondary">See what we measured</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Partners;
